// theme/applyPreferences.ts
//
// Pure function that merges personalisation preferences onto a base ThemeTokens
// object. No mutation of `base` — always returns a new object.
//
// Applies: accent colour (preset OR custom via the Appearance colour wheel), accent
// finish, the card appearance system (colour + material + glass), text size, and the
// numeric corner radius. (fontFamily remains retired — the type system is fixed in
// tokens.ts.)
//
// CARD COLOUR SAFETY: the user's picked colour is a hue/chroma SEED only. Its lightness
// is re-solved into a per-mode WCAG luminance band (see presets/cardSurface.ts) before it
// reaches `surface` / `surfaceRaised` / `glassBg`, so no pick can push body or muted text
// below AA. Because all three tokens derive from ONE resolved fill, every consumer
// inherits the guarantee — no component opts in.

import type { ThemeTokens } from './tokens';
import type { UserPreferences } from '@/core/models/preferences';
import { clampRadiusHard } from '@/core/models/preferences';
import { TEXT_SIZE_MULTIPLIERS } from './presets/textSize';
import { glassBg } from './presets/panelSolidity';
import { ACCENT_PRESETS, type AccentColors } from './presets/accents';
import { rgba, onAccentFor, darken, lighten, flatten } from './presets/color';
import { resolveMaterial } from './presets/cardMaterial';
import {
  solveBand,
  resolveCardSurface,
  resolveCardRaised,
  ensureNoWorse,
  effectiveLuminance,
} from './presets/cardSurface';

// Scale a single CSS pixel value string by a multiplier. "15px" → 1.12 → "16.8px"
function scalePx(value: string, mult: number): string {
  const n = parseFloat(value);
  if (isNaN(n)) return value;
  return `${Math.round(n * mult * 10) / 10}px`;
}

/** Resolve the five brand/accent colours for the active accent pref + mode. */
function resolveAccent(prefs: UserPreferences, mode: 'light' | 'dark'): AccentColors {
  if (prefs.accent === 'custom') {
    const c = prefs.accentCustom;
    // Derive a full ramp from the single picked colour.
    return {
      brandFrom: c,
      brandTo: darken(c, 0.25),
      accentCyan: c,
      accentMagenta: lighten(c, 0.12),
      accentAmber: darken(c, 0.15),
    };
  }
  return ACCENT_PRESETS[prefs.accent][mode];
}

export function applyPreferences(base: ThemeTokens, prefs: UserPreferences): ThemeTokens {
  const sizeMult = TEXT_SIZE_MULTIPLIERS[prefs.textSize];

  // Glass cards when the overlay pref is OFF (background shows through).
  // The accessibility Reduce-transparency switch OVERRIDES glass without touching
  // the stored Appearance prefs — switching it back off restores the user's glass
  // configuration exactly as they left it.
  const cardsGlass = !prefs.cardsOverlay && !prefs.reduceTransparency;

  // ── Accent: apply the chosen palette + derive the dependent tint tokens. ──
  const a = resolveAccent(prefs, base.mode);
  const primary = a.accentCyan;
  const glowAlpha = base.mode === 'dark' ? 0.3 : 0.16;

  // ── Card appearance: material first (it shifts the rendered luminance), then the
  //    band, then the fill solved so the COMPOSITED card lands inside the band. ──
  const material = resolveMaterial(base.mode, prefs);
  const band = solveBand(base.mode, base.color.textPrimary, base.color.textMuted, primary);
  const cardFill = resolveCardSurface(
    base.mode,
    base.color.surface,
    prefs.cardColor,
    prefs.cardColorStrength,
    material,
    band,
  );

  // Glass ON: translucent over the resolved card colour. Glass OFF: the opaque card hex
  // itself — a hex has no alpha channel, so the animated backdrop cannot bleed through.
  // (Glass ON degrades the band guarantee to an approximation, since the backdrop's
  // luminance is unknowable at theme-build time; the whole panelSolidity range was
  // verified to land in band against bgRoot.)
  const glassFill = cardsGlass
    ? glassBg(base.mode, prefs.panelSolidity, prefs.glassTint, primary, cardFill)
    : cardFill;

  // Luminance of the card as actually read, used only by the textFaint guard.
  const readL = effectiveLuminance(
    cardsGlass ? flatten(glassFill, base.color.bgRoot) : cardFill,
    base.mode,
    material,
  );

  const customCard = prefs.cardColorStrength > 0;
  const radiusPx = clampRadiusHard(prefs.radiusPx); // HARD limits only — never the soft bounds
  const radiusMult = radiusPx / parseFloat(base.radius.lg);
  const seeds =
    prefs.overlayGradient === 'sunset'
      ? ['#BF477A', '#C17A44']
      : prefs.overlayGradient === 'accent'
        ? [a.brandFrom, a.brandTo]
        : ['#8241E8', '#394BBA'];
  // Both endpoints pass through the same luminance solver as the card fill.
  // Interpolate in sRGB; in glass mode retain the user's chosen opacity.
  const gradientFor = (strength: number): string => {
    if (prefs.overlayGradient === 'none' || !strength) return 'none';
    const gradientStops = seeds.map((seed) => {
      const fill = resolveCardSurface(base.mode, cardFill, seed, strength, material, band);
      return cardsGlass ? glassBg(base.mode, prefs.panelSolidity, 0, primary, fill) : fill;
    });
    return `linear-gradient(135deg, ${gradientStops[0]} 0%, ${glassFill} 56%, ${gradientStops[1]} 100%)`;
  };
  const surfaceGradients = {
    reading: gradientFor(prefs.postGradientStrength),
    panel: gradientFor(prefs.panelGradientStrength),
    overlay: gradientFor(prefs.overlayGradientStrength),
  };

  return {
    ...base,
    finish: prefs.finish, // accent fills only (Button primary + elevation.glow)
    cardsGlass,
    surfaceGradient: surfaceGradients.overlay,
    surfaceGradients,
    surfaceRole: 'reading',
    // Card backdrop-filter knobs (only consumed by the card primitives when
    // cardsGlass is true) — user-adjustable blur + saturation.
    glassBlur: prefs.glassBlur,
    glassSaturation: prefs.glassSaturation,
    material,
    underlineLinks: prefs.underlineLinks,

    color: {
      ...base.color,
      brandFrom: a.brandFrom,
      brandTo: a.brandTo,
      accentCyan: a.accentCyan,
      accentCyanDim: a.brandTo,
      accentMagenta: a.accentMagenta,
      accentAmber: a.accentAmber,
      // glassBorder is NOT re-tinted from the accent any more. An accent-hued
      // hairline on every card, rail and divider is what made the whole screen
      // read as one colour wash; hairlines are neutral and inherited from the
      // base theme so the accent stays a mark on four controls.
      glow: rgba(primary, glowAlpha),
      focusRing: rgba(primary, 1),
      onAccent: onAccentFor(primary),
      surface: cardFill,
      // Only step surfaceRaised away from the card when the user actually customised the
      // colour — otherwise keep the shipped token verbatim.
      surfaceRaised: customCard
        ? resolveCardRaised(base.mode, cardFill, band)
        : base.color.surfaceRaised,
      glassBg: glassFill,
      // The one text token this design moves — and only ever in the safe direction.
      textFaint: ensureNoWorse(base.color.textFaint, readL, base.color.surface, 3, base.mode),
    },

    elevation: {
      ...base.elevation,
      // `glow` is the FOCUS ring, not decoration: a 2px accent ring held off the
      // element by a 2px ring in the card's own colour, so it stays visible
      // against any user-picked card colour and any accent preset. The 'glow'
      // finish pref still differs — it adds the old halo on top of the ring,
      // because that is a look the user opted into, not the default.
      glow:
        prefs.finish === 'glow'
          ? `0 0 0 2px ${cardFill}, 0 0 0 4px ${rgba(primary, 1)}, 0 0 26px ${rgba(primary, Math.min(glowAlpha + 0.2, 0.6))}`
          : `0 0 0 2px ${cardFill}, 0 0 0 4px ${rgba(primary, 1)}`,
    },

    // Font sizes and their bound line boxes scale together.
    fontSize: {
      xs: scalePx(base.fontSize.xs, sizeMult),
      sm: scalePx(base.fontSize.sm, sizeMult),
      md: scalePx(base.fontSize.md, sizeMult),
      lg: scalePx(base.fontSize.lg, sizeMult),
      xl: scalePx(base.fontSize.xl, sizeMult),
      display: scalePx(base.fontSize.display, sizeMult),
    },
    lineHeight: {
      xs: scalePx(base.lineHeight.xs, sizeMult),
      sm: scalePx(base.lineHeight.sm, sizeMult),
      md: scalePx(base.lineHeight.md, sizeMult),
      lg: scalePx(base.lineHeight.lg, sizeMult),
      xl: scalePx(base.lineHeight.xl, sizeMult),
      display: scalePx(base.lineHeight.display, sizeMult),
    },

    // Radius: lg is the user's exact px; sm/md scale by the same ratio; pill untouched.
    radius: {
      sm: scalePx(base.radius.sm, radiusMult),
      md: scalePx(base.radius.md, radiusMult),
      lg: `${radiusPx}px`,
      pill: base.radius.pill,
    },
  };
}
